import { Body1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import type { ComponentType, SVGProps } from "react";

export type GhostEmptyVariant = "default" | "search";

const COPY: Record<GhostEmptyVariant, { title: string; description: string }> = {
  default: {
    title: "여기는 아직 텅 비어 있어요",
    description: "항목이 생기면 이곳에 차곡차곡 모여요.",
  },
  search: {
    title: "찾는 항목이 없어요",
    description: "검색어나 조건을 조금 바꿔볼까요?",
  },
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalL}`,
    textAlign: "center",
    userSelect: "none",
    boxSizing: "border-box",
  },
  // 이 요소만 남은 높이를 차지할 때 문구를 세로 가운데에 둔다. compact는 대화상자·표 안이라 늘어나지 않는다.
  fill: {
    ":only-child": {
      flexGrow: 1,
      flexShrink: 1,
      alignSelf: "stretch",
      minHeight: "100%",
    },
  },
  compact: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
  },
  iconWrap: {
    width: "48px",
    height: "48px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: tokens.spacingVerticalS,
  },
  icon: {
    animationName: {
      "0%, 100%": { transform: "translateY(0)" },
      "50%": { transform: "translateY(-5px)" },
    },
    animationDuration: "1.8s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
  },
  title: {
    color: tokens.colorNeutralForeground2,
  },
  description: {
    color: tokens.colorNeutralForeground3,
    maxWidth: "100%",
    marginTop: tokens.spacingVerticalXXS,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

export function GhostEmptyState({
  variant = "default",
  title,
  description,
  topText,
  bottomText,
  compact = false,
  showIcon = true,
  icon: Icon = GhostGlyph,
  className,
}: {
  variant?: GhostEmptyVariant;
  title?: string;
  description?: string;
  topText?: string;
  bottomText?: string;
  compact?: boolean;
  showIcon?: boolean;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  className?: string;
}) {
  const styles = useStyles();
  const fallback = COPY[variant];
  const resolvedTitle = topText ?? title ?? fallback.title;
  const resolvedDescription = bottomText ?? description ?? (topText == null && title == null ? fallback.description : undefined);

  return (
    <div className={mergeClasses(styles.root, compact ? styles.compact : styles.fill, className)} role="status">
      {showIcon ? (
        <div className={styles.iconWrap} aria-hidden>
          <GhostIcon className={styles.icon} Icon={Icon} />
        </div>
      ) : null}
      <Body1 className={styles.title}>{resolvedTitle}</Body1>
      {resolvedDescription ? <Body1 className={styles.description}>{resolvedDescription}</Body1> : null}
    </div>
  );
}

function GhostGlyph({ className, width = 22, height = 22, strokeWidth = 1.5, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={width}
      height={height}
      className={className}
      aria-hidden
      {...props}
    >
      <path d="M9 10h.01" />
      <path d="M15 10h.01" />
      <path d="M12 2a8 8 0 0 0-8 8v12l3-3 2.5 2.5L12 19l2.5 2.5L17 19l3 3V10a8 8 0 0 0-8-8z" />
    </svg>
  );
}

function GhostIcon({
  className,
  Icon,
}: {
  className: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}) {
  return <Icon className={className} width={22} height={22} strokeWidth={1.5} />;
}
